"use client";

import { LinearGradient } from "@visx/gradient";
import { Group } from "@visx/group";
import { scaleBand, scaleLinear } from "@visx/scale";
import { max } from "d3-array";
import { motion } from "framer-motion";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import type { TooltipData, YFormatType } from "../../../context/ChartContext";
import { getMaxTextWidth } from "../../../utils/measureText";
import { CATEGORY_LABEL_FONT_SIZE, ChartAxis } from "../ChartAxis";
import { ChartTooltip } from "../ChartTooltip";

export interface CategoryDataPoint {
  /** Category label */
  label: string;
  /** Numeric value */
  value: number;
  /** Optional color override for this bar */
  color?: string;
}

export interface CategoryBarChartProps {
  /** Array of data points with labels and values */
  data: CategoryDataPoint[];
  /** Width of the chart (auto-resizes to container if not provided) */
  width?: number;
  /** Height of the chart (auto-resizes to container if not provided) */
  height?: number;
  /** Scale the fixed coordinate system to the container width for static displays. Requires both width and height; otherwise uses normal responsive sizing. Disables tooltips when active. */
  scaleToFit?: boolean;
  /** Margin around the chart area */
  margin?: { top: number; right: number; bottom: number; left: number };
  /** Single color for all bars (uses theme color if not provided) */
  color?: string;
  /** Formatter for numeric axis values (uses standard chart formatting system) */
  yFormatter?: YFormatType;
  /** Show grid lines */
  showGrid?: boolean;
  /** Show axis labels */
  showAxes?: boolean;
  /** Animate bars on mount */
  animate?: boolean;
  /** Bar corner radius */
  barRadius?: number;
  /** Inner padding between bars (0-1) */
  barPadding?: number;
  /** Callback when a bar is clicked */
  onBarClick?: (dataPoint: CategoryDataPoint, index: number) => void;
  /** Callback when a bar is hovered */
  onBarHover?: (dataPoint: CategoryDataPoint | null, index: number | null) => void;
  /** Optional external active bar index */
  activeIndex?: number | null;
  /** Additional className */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
  /** Accessible label */
  /**
   * The chart's accessible name. Required, not optional-with-a-default: a default
   * means every categorical chart announces identically, which is the bug ALL-2539
   * fixed in the two time-series engines. Required makes the type checker the
   * alarm, so a chart cannot be added without one (ALL-2715).
   */
  "aria-label": string;
  /** Orientation: horizontal (default) or vertical */
  orientation?: "vertical" | "horizontal";
  /** Show tooltip on hover (default true) */
  showTooltip?: boolean;
}

/**
 * `left` here is sized for the *vertical* orientation, where the left axis holds
 * short numeric ticks. Horizontal charts derive their own — see
 * `useResolvedMargin`.
 */
const defaultMargin = { top: 20, right: 20, bottom: 50, left: 60 };

/** Tick mark plus the gap to the plot. Matches `ChartContainer`'s allowance. */
const AXIS_LABEL_GUTTER = 12;

/**
 * Ceiling on the label gutter, as a share of total width.
 *
 * Category labels are arbitrary-length text, so "just fit them" has no upper
 * bound — one 60-character name would squeeze every bar into a sliver and
 * destroy the comparison the chart exists to make. Past this share the labels
 * truncate instead of taking more room.
 */
const MAX_CATEGORY_AXIS_FRACTION = 0.4;

/** Floor for the gutter, so narrow containers are no worse off than before. */
const MIN_CATEGORY_AXIS_WIDTH = 60;

/**
 * The margin the chart actually draws with.
 *
 * A horizontal bar chart puts category names in the left gutter, but the gutter
 * was a constant 60px sized for numeric ticks — so any longer name was clipped
 * by the container's `overflow-hidden` and the plot never gave the ground back.
 * Measuring the labels lets the plot take whatever width is left, which is what
 * makes a long name visible at all.
 *
 * An explicit `margin` prop still wins: a caller that has sized its own gutter
 * has context this cannot see.
 */
function useResolvedMargin({
  margin,
  orientation,
  labelsKey,
  width,
}: {
  margin: CategoryBarChartProps["margin"];
  orientation: "vertical" | "horizontal";
  /** Category labels joined by NUL — a value-identity key for the memo. */
  labelsKey: string;
  width: number;
}) {
  return useMemo(() => {
    if (margin) return margin;
    if (orientation !== "horizontal") return defaultMargin;

    const widest = getMaxTextWidth(labelsKey.split("\u0000"), CATEGORY_LABEL_FONT_SIZE, "system-ui");
    // Guard the fraction against a container that has not measured yet: 40% of 0
    // would collapse the gutter to nothing and clip every label.
    const ceiling = Math.max(MIN_CATEGORY_AXIS_WIDTH, Math.floor(width * MAX_CATEGORY_AXIS_FRACTION));
    const fitted = Math.ceil(widest) + AXIS_LABEL_GUTTER;

    return {
      ...defaultMargin,
      // Floor as well as cap. Fitting *tightly* is its own regression: a chart of
      // short labels would get a 41px gutter where it used to have 60, so the
      // axis would hug the text and the bars would shift under it between
      // renders as the data changed. The floor keeps this a widening-only change.
      left: Math.min(Math.max(fitted, MIN_CATEGORY_AXIS_WIDTH), ceiling),
    };
  }, [margin, orientation, labelsKey, width]);
}

export const CategoryBarChart: React.FC<CategoryBarChartProps> = ({
  data,
  width: propWidth,
  height: propHeight,
  margin: marginProp,
  scaleToFit = false,
  color,
  yFormatter = { type: "number" },
  showGrid = true,
  showAxes = true,
  animate = true,
  barRadius = 4,
  barPadding = 0.3,
  onBarClick,
  onBarHover,
  activeIndex,
  className,
  style,
  "aria-label": ariaLabel,
  orientation = "horizontal",
  showTooltip = true,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const [tooltipLabel, setTooltipLabel] = useState<string | null>(null);
  const useFixedScaling = scaleToFit && propWidth !== undefined && propHeight !== undefined;
  const [actualWidth, setActualWidth] = useState(propWidth ?? 600);
  const [actualHeight, setActualHeight] = useState(propHeight ?? 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const resolvedActiveIndex = activeIndex ?? hoveredIndex;
  const gradientId = useId();
  const baseColor = color || "var(--color-viz-default)";

  useEffect(() => {
    if (propWidth !== undefined && propHeight !== undefined) {
      setActualWidth(propWidth);
      setActualHeight(propHeight);
      return;
    }

    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (propWidth === undefined) {
          setActualWidth(entry.contentRect.width);
        }
        if (propHeight === undefined) {
          setActualHeight(entry.contentRect.height);
        }
      }
    });

    resizeObserver.observe(containerRef.current);
    if (propWidth === undefined) {
      setActualWidth(containerRef.current.offsetWidth);
    }
    if (propHeight === undefined) {
      setActualHeight(containerRef.current.offsetHeight || 300);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [propWidth, propHeight]);

  // Keyed on label *values*, not the `data` array's identity: a hover or a
  // parent re-render hands us a fresh array of the same categories, and
  // remeasuring there would rebuild every scale downstream of the margin.
  const labelsKey = data.map((d) => d.label).join("\u0000");

  const margin = useResolvedMargin({ margin: marginProp, orientation, labelsKey, width: actualWidth });

  /** Room the category labels have before they must truncate. */
  const categoryLabelWidth = Math.max(0, margin.left - AXIS_LABEL_GUTTER);

  const { xScale, yScale, innerWidth, innerHeight } = useMemo(() => {
    const innerWidth = actualWidth - margin.left - margin.right;
    const innerHeight = actualHeight - margin.top - margin.bottom;

    if (orientation === "horizontal") {
      const xMax = max(data, (d) => d.value) || 0;

      return {
        xScale: scaleLinear<number>({
          range: [0, innerWidth],
          domain: [0, xMax * 1.1],
          nice: true,
        }),
        yScale: scaleBand<string>({
          range: [0, innerHeight],
          domain: data.map((d) => d.label),
          padding: barPadding,
        }),
        innerWidth,
        innerHeight,
      };
    }

    const yMax = max(data, (d) => d.value) || 0;

    return {
      xScale: scaleBand<string>({
        range: [0, innerWidth],
        domain: data.map((d) => d.label),
        padding: barPadding,
      }),
      yScale: scaleLinear<number>({
        range: [innerHeight, 0],
        domain: [0, yMax * 1.1],
        nice: true,
      }),
      innerWidth,
      innerHeight,
    };
  }, [data, actualWidth, actualHeight, margin, barPadding, orientation]);

  if (data.length === 0) {
    return (
      <div
        ref={containerRef}
        className={twMerge(
          "flex h-full min-h-[200px] w-full items-center justify-center rounded-lg border border-dashed border-border-default bg-background-surface text-sm font-medium text-text-secondary",
          className
        )}
        style={propHeight !== undefined ? { height: propHeight, ...style } : style}
      >
        No data available
      </div>
    );
  }

  const handleBarHover = (index: number | null, dataPoint: CategoryDataPoint | null) => {
    setHoveredIndex(index);
    onBarHover?.(dataPoint, index);

    if (index !== null && dataPoint) {
      // Calculate tooltip position based on bar position
      let tooltipX: number;
      let tooltipY: number;

      if (orientation === "horizontal") {
        const barWidth = (xScale as ReturnType<typeof scaleLinear<number>>)(dataPoint.value);
        const barY = (yScale as ReturnType<typeof scaleBand<string>>)(dataPoint.label) || 0;
        const barHeight = (yScale as ReturnType<typeof scaleBand<string>>).bandwidth();
        tooltipX = margin.left + barWidth;
        tooltipY = margin.top + barY + barHeight / 2;
      } else {
        const bandScale = xScale as ReturnType<typeof scaleBand<string>>;
        const linearScale = yScale as ReturnType<typeof scaleLinear<number>>;
        const barX = bandScale(dataPoint.label) ?? 0;
        const barWidth = bandScale.bandwidth();
        const barY = linearScale(dataPoint.value);
        tooltipX = margin.left + barX + barWidth / 2;
        tooltipY = margin.top + barY;
      }

      const shouldShowLeft = tooltipX > actualWidth / 2;
      setTooltipLabel(dataPoint.label);
      setTooltipData({
        xValue: new Date(), // Dummy date, label is passed separately
        series: [
          {
            label: "Value",
            value: dataPoint.value,
            color: dataPoint.color || baseColor,
          },
        ],
        x: tooltipX,
        y: tooltipY,
        position: shouldShowLeft ? "left" : "right",
      });
    } else {
      setTooltipData(null);
      setTooltipLabel(null);
    }
  };

  return (
    <div
      ref={containerRef}
      className={twMerge(
        "relative w-full overflow-hidden rounded-lg bg-background-surface",
        !useFixedScaling && "h-full",
        className
      )}
      style={!useFixedScaling && propHeight !== undefined ? { height: propHeight, ...style } : style}
      aria-label={ariaLabel}
      role="img"
    >
      <svg
        width={actualWidth}
        height={actualHeight}
        viewBox={useFixedScaling ? `0 0 ${actualWidth} ${actualHeight}` : undefined}
        style={useFixedScaling ? { display: "block", width: "100%", height: "auto" } : undefined}
      >
        <defs>
          <LinearGradient id={`${gradientId}-bar`} from={baseColor} to={baseColor} toOpacity={0.85} />
        </defs>

        <Group left={margin.left} top={margin.top}>
          {showGrid && (
            <g className="grid-lines">
              {(orientation === "horizontal"
                ? (xScale as ReturnType<typeof scaleLinear<number>>).ticks(5)
                : (yScale as ReturnType<typeof scaleLinear<number>>).ticks(5)
              ).map((tickValue) => {
                const y =
                  orientation === "horizontal" ? 0 : (yScale as ReturnType<typeof scaleLinear<number>>)(tickValue);
                const x =
                  orientation === "horizontal" ? (xScale as ReturnType<typeof scaleLinear<number>>)(tickValue) : 0;
                return (
                  <line
                    key={tickValue}
                    x1={orientation === "horizontal" ? x : 0}
                    y1={orientation === "horizontal" ? 0 : y}
                    x2={orientation === "horizontal" ? x : innerWidth}
                    y2={orientation === "horizontal" ? innerHeight : y}
                    stroke="var(--color-border-default)"
                    strokeOpacity={0.5}
                    strokeDasharray="2,3"
                  />
                );
              })}
            </g>
          )}

          {data.map((d, i) => {
            const isActive = resolvedActiveIndex === i || resolvedActiveIndex == null;
            const barColor = d.color || `url(#${gradientId}-bar)`;

            if (orientation === "horizontal") {
              const barWidth = (xScale as ReturnType<typeof scaleLinear<number>>)(d.value);
              const barHeight = (yScale as ReturnType<typeof scaleBand<string>>).bandwidth();
              const barY = (yScale as ReturnType<typeof scaleBand<string>>)(d.label) || 0;

              return (
                <motion.rect
                  key={d.label}
                  x={0}
                  y={barY}
                  width={barWidth}
                  height={barHeight}
                  fill={barColor}
                  rx={barRadius}
                  ry={barRadius}
                  initial={animate ? { width: 0, opacity: 0.5 } : undefined}
                  animate={{ width: barWidth, opacity: isActive ? 1 : 0.5 }}
                  transition={{
                    duration: 0.5,
                    delay: i * 0.05,
                    ease: "easeOut",
                    opacity: { duration: 0.15, delay: 0 },
                  }}
                  style={{
                    cursor: onBarClick || onBarHover ? "pointer" : "default",
                  }}
                  onMouseEnter={() => handleBarHover(i, d)}
                  onMouseLeave={() => handleBarHover(null, null)}
                  onClick={() => onBarClick?.(d, i)}
                />
              );
            }

            const bandScale = xScale as ReturnType<typeof scaleBand<string>>;
            const linearScale = yScale as ReturnType<typeof scaleLinear<number>>;

            const barWidth = bandScale.bandwidth();
            const barX = bandScale(d.label) ?? 0;
            const barY = linearScale(d.value);
            const barHeight = Math.max(0, innerHeight - barY);

            if (animate) {
              return (
                <motion.rect
                  key={d.label}
                  x={barX}
                  width={barWidth}
                  fill={barColor}
                  rx={barRadius}
                  ry={barRadius}
                  initial={{ y: innerHeight, height: 0, opacity: 0.5 }}
                  animate={{
                    y: barY,
                    height: barHeight,
                    opacity: isActive ? 1 : 0.5,
                  }}
                  transition={{
                    duration: 0.5,
                    delay: i * 0.05,
                    ease: "easeOut",
                    opacity: { duration: 0.15, delay: 0 },
                  }}
                  style={{
                    cursor: onBarClick || onBarHover ? "pointer" : "default",
                  }}
                  onMouseEnter={() => handleBarHover(i, d)}
                  onMouseLeave={() => handleBarHover(null, null)}
                  onClick={() => onBarClick?.(d, i)}
                />
              );
            }

            return (
              <rect
                key={d.label}
                x={barX}
                y={barY}
                width={barWidth}
                height={barHeight}
                fill={barColor}
                rx={barRadius}
                ry={barRadius}
                opacity={isActive ? 1 : 0.5}
                style={{
                  cursor: onBarClick || onBarHover ? "pointer" : "default",
                  transition: "opacity 150ms ease-out",
                }}
                onMouseEnter={() => handleBarHover(i, d)}
                onMouseLeave={() => handleBarHover(null, null)}
                onClick={() => onBarClick?.(d, i)}
              />
            );
          })}

          {showAxes && orientation === "vertical" && (
            <>
              <ChartAxis.CategoryBottom top={innerHeight} scale={xScale as ReturnType<typeof scaleBand<string>>} />
              <ChartAxis.Left
                left={0}
                scale={yScale as ReturnType<typeof scaleLinear<number>>}
                yFormatter={yFormatter}
                height={innerHeight}
              />
            </>
          )}

          {showAxes && orientation === "horizontal" && (
            <>
              <ChartAxis.NumericBottom
                top={innerHeight}
                scale={xScale as ReturnType<typeof scaleLinear<number>>}
                yFormatter={yFormatter}
                width={innerWidth}
              />
              <ChartAxis.CategoryLeft
                left={0}
                labelLeft={-margin.left}
                scale={yScale as ReturnType<typeof scaleBand<string>>}
                availableWidth={categoryLabelWidth}
              />
            </>
          )}
        </Group>
      </svg>

      {/* Tooltip */}
      {showTooltip && !useFixedScaling && (
        <ChartTooltip data={tooltipData} formatter={yFormatter} label={tooltipLabel ?? undefined} />
      )}
    </div>
  );
};

export type { CategoryDataPoint as CategoryBarChartDataPoint };
